import { type TTlds, type BaseJsonConfiguration } from '@dhl/receiver-config'

export const portalTlds = ['nl'] as const
export type PortalTlds = typeof portalTlds

export type MopinionSurveyConfiguration = {
  surveyEndpoint?: string
  token: string
  surveyId: number
  surveyKey?: string
}

export type PortalAppConfiguration = {
  apiGatewayHost: string
  authentication: {
    loginPath: string
    signinPath: string
    logoutPath: string
  }
  basePath: string
  features: {
    c2c: {
      active: boolean
      activationPercentage: number
      askForPrintlessPhoto: {
        active: boolean
      }
      entrySummaryDeclaration: {
        active: boolean
      }
      reserveLocker: {
        active: boolean
      }
    }
    dashboard: {
      active: boolean
    }
    receive: {
      active: boolean
      emptyState: string
      consolidatedPieces: {
        active: boolean
      }
    }
    send: {
      active: boolean
      outgoingPiecesMax: number
    }
    more: {
      active: boolean
      claims: {
        active: boolean
      }
      goGreen: {
        active: boolean
      }
      compliments: {
        active: boolean
      }
      joinDhl: {
        active: boolean
      }
    }
    profile: {
      active: boolean
      maxEmailAddresses: number
      mobileNumber: {
        active: boolean
        countryFilter: string[]
        required: boolean
      }
      deliveryPreferences: {
        active: boolean
        agreedPlacePhotoUpload: {
          active: boolean
        }
        deliveryPreferencesV2: {
          active: boolean
        }
      }
      marketingPreferences: {
        active: boolean
      }
      notificationPreferences: {
        active: boolean
        email: {
          active: boolean
        }
        push: {
          active: boolean
          sender: {
            active: boolean
          }
          receiver: {
            active: boolean
          }
        }
      }
    }
    chat: {
      active: boolean
      chatbotUri: string
      useAuthenticatedConversations: boolean
    }
    interventions: {
      active: boolean
      showServicepointLocatorMap: {
        active: boolean
      }
      useNeighbourInterventionStreetLookup: {
        active: boolean
      }
      useLockerNudge: {
        active: boolean
      }
      useNeighbourFeedback: {
        active: boolean
      }
    }
    trackTrace: {
      active: boolean
      receiverHost: string
      share: {
        active: boolean
      }
      rating: {
        active: boolean
      }
      returnV2View: {
        active: boolean
      }
      returnWithCourier: {
        active: boolean
      }
    }
    engagement: {
      active: boolean
    }
    interaction: {
      active: boolean
      guidedTour: {
        active: boolean
      }
      agreedPlacePreferencePhotoDialog: {
        active: boolean
      }
      validateMobileNumber: {
        active: boolean
      }
    }
    nativeUpdateDialog: {
      active: boolean
      advisedNativeAppVersion: string
      mandatoryNativeAppVersion: string
    }
    newProfileSetup: {
      active: boolean
      enabledDate: string
    }
    maintenance: {
      active: boolean
    }
    loyalty: {
      active: boolean
    }
    dhlExtra: {
      active: boolean
    }
    voucher: {
      active: boolean
    }
  }
  adobe: {
    url: string
  }
  datadog: {
    applicationId: string
    clientToken: string
    service: string
    logsSessionSampleRate: number
    rumSessionSampleRate: number
    rumSessionReplaySampleRate: number
  }
  gtm: {
    id: string
  }
  maps: {
    apiKey: string
  }
  migration: {
    mode: string
  }
  mopinion: {
    surveyEndpoint: string
    token: string
    feedbackSurveyId: number
  }
  mopinionSurveys: {
    surveyEndpoint: string
    feedback: MopinionSurveyConfiguration
    nps: MopinionSurveyConfiguration
    returnFeedback: MopinionSurveyConfiguration
    c2cFeedback: MopinionSurveyConfiguration
    neighbourInterventionCompletedFeedback: MopinionSurveyConfiguration
    neighbourInterventionCommentFeedback: MopinionSurveyConfiguration
  }
  native: {
    // TODO: this should be listed under features.
    inAppReview: {
      minimumUsageCount: number
      minimumInstalledDays: number
      askAgainAfterDays: number
    }
  }
  oneTrust: {
    id: string
    enableForNative: boolean
    enableForNativeFromVersion: string
  }
  prismic: {
    endpoint: string
  }
  postHog: {
    apiKey: string
  }
  util: {
    tldOverride?: string
  }
}

export type JsonConfiguration<
  TSupportedTlds extends TTlds,
  TAppConfiguration = PortalAppConfiguration,
> = BaseJsonConfiguration<TSupportedTlds, TAppConfiguration> & {
  BASE_PATH: string
}
